import React from "react";
import { motion } from "framer-motion";

// One button per word, laid out right-to-left for Hebrew. Tap a word to hear it
// on its own; its letters dim as they're played. Deliberately the same squared,
// bordered button language as the rest of the site.
const isHebrew = (s) => /[\u0590-\u05FF]/.test(s || "");

function WordButton({
  word,
  wordIdx,
  droppedSet,
  onWordClick,
  isPreviewing,
  interactive,
  inviteClicks,
}) {
  const heb = isHebrew(word);
  return (
    <motion.button
      type="button"
      data-testid={`word-${wordIdx}`}
      onClick={() => interactive && onWordClick && onWordClick(wordIdx)}
      disabled={!interactive}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
      whileHover={interactive ? { y: -2 } : undefined}
      className="relative inline-flex items-center rounded-full px-4 py-2.5 transition-colors disabled:cursor-default"
      style={{
        direction: heb ? "rtl" : "ltr",
        fontFamily: heb ? "'Frank Ruhl Libre', serif" : "'Courier Prime', monospace",
        border: `1px solid ${isPreviewing ? "#D99B26" : "#EBE3D5"}`,
        backgroundColor: isPreviewing ? "rgba(217,155,38,0.14)" : "#FFFFFF",
        cursor: interactive ? "pointer" : "default",
      }}
    >
      {[...word].map((ch, i) => {
        const dropped = droppedSet.has(`${wordIdx}:${i}`);
        return (
          <span
            key={`${wordIdx}-${i}-${ch}`}
            data-testid={`word-${wordIdx}-letter-${i}`}
            className="inline-block text-[26px] leading-none tracking-[0.03em] transition-opacity duration-200"
            style={{
              color: isPreviewing ? "#D99B26" : "#1A1523",
              opacity: isPreviewing ? 1 : dropped ? 0.28 : 1,
            }}
          >
            {ch}
          </span>
        );
      })}
      {inviteClicks && !isPreviewing && (
        <span
          aria-hidden="true"
          className="absolute -top-1.5 -right-1.5 w-4 h-4 flex items-center justify-center text-[9px]"
          style={{ backgroundColor: "#FFFBF2", border: "1px solid #E86F4A", color: "#E86F4A" }}
        >
          ↻
        </span>
      )}
    </motion.button>
  );
}

export default function Fridge({
  words,
  droppedSet,
  onWordClick,
  previewingWordIdx = -1,
  interactive = true,
  hasPlayed = false,
}) {
  const live = words.filter((w) => w && w.trim().length > 0);
  const rtl = live.some(isHebrew);

  return (
    <div
      data-testid="fridge-surface"
      className="w-full rounded-md border p-5 sm:p-6"
      style={{ borderColor: "#EBE3D5", backgroundColor: "#FFFBF2", minHeight: 140 }}
    >
      {live.length === 0 ? (
        <div
          className="flex items-center justify-center text-sm text-center py-10"
          style={{ color: "#5C5268", fontFamily: "'Courier Prime', monospace" }}
        >
          Your words will appear here.
        </div>
      ) : (
        <div
          className="flex flex-wrap gap-2.5"
          style={{ direction: rtl ? "rtl" : "ltr" }}
        >
          {words.map((w, i) =>
            w && w.trim().length > 0 ? (
              <WordButton
                key={`${i}-${w}`}
                word={w}
                wordIdx={i}
                droppedSet={droppedSet}
                onWordClick={onWordClick}
                isPreviewing={previewingWordIdx === i}
                interactive={interactive}
                inviteClicks={hasPlayed}
              />
            ) : null
          )}
        </div>
      )}
    </div>
  );
}
